import PropTypes from 'prop-types';
import {
	Alert,
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';

import EmailRuleSection from './EmailRuleSection';

const EmailRules = ({
	tenantLevel,
	inclusionEnabled,
	handleRuleTypeChange,
	isRunning,
	criteriaType,
	setCriteriaType,
	criteriaValue,
	setCriteriaValue,
	handleAddEmailRule,
	handleDeleteEmailRule,
	domainRules,
	senderTypeRules,
	specificEmailRules,
	hasConfig,
	reportId,
}) => {
	const theme = useTheme();

	const getCriteriaLabel = (criteriaType) => {
		switch (criteriaType) {
			case 'domain':
				return 'Domain (e.g., example.com)';
			case 'sender_type':
				return 'Sender Type (e.g., no-reply)';
			case 'specific_email':
				return 'Email Address';
			default:
				return 'Email Address';
		}
	};

	return (
		<>
			<Typography
				variant='subtitle1'
				sx={{
					fontWeight: 600,
					fontSize: '0.9rem',
					textTransform: 'uppercase',
					letterSpacing: '0.5px',
				}}
				gutterBottom
			>
				Email Rules
			</Typography>

			<Box>
				<Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
					<Typography variant='body2' color='text.secondary'>
						Current Rule Type:
					</Typography>

					<Typography sx={{ fontWeight: inclusionEnabled ? 400 : 600 }}>
						Exclusion
					</Typography>

					{tenantLevel ? (
						<Switch
							checked={inclusionEnabled}
							onChange={handleRuleTypeChange}
							disabled={isRunning}
						/>
					) : (
						<Tooltip
							title='This setting is controlled by your tenant admin'
							slotProps={{
								tooltip: {
									sx: {
										borderRadius: '5px',
									},
								},
							}}
						>
							<span>
								<Switch
									sx={{
										m: 0,
										'& .MuiSwitch-track': {
											backgroundColor: '#8C8EFF',
										},
									}}
									checked={inclusionEnabled}
									disabled
								/>
							</span>
						</Tooltip>
					)}

					<Typography sx={{ fontWeight: inclusionEnabled ? 600 : 400 }}>
						Inclusion
					</Typography>
				</Stack>

				<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
					Emails matching these rules will {inclusionEnabled ? 'be' : 'not be'}{' '}
					synced to Altvia.
				</Typography>
			</Box>

			{(tenantLevel || !inclusionEnabled) && (
				<>
					<Alert
						severity='info'
						sx={{
							mb: 2,
							backgroundColor: theme.palette.background.lightBlue,
						}}
					>
						Changes to email rules will be applied to new ingestion jobs, but
						not to existing ones.
					</Alert>

					<Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
						<FormControl sx={{ minWidth: 200, mr: 2 }}>
							<InputLabel id='criteria-type-label'>Criteria Type</InputLabel>

							<Select
								labelId='criteria-type-label'
								value={criteriaType}
								label='Criteria Type'
								onChange={(e) => setCriteriaType(e.target.value)}
								disabled={isRunning}
							>
								<MenuItem
									value='domain'
									disabled={hasConfig || Boolean(reportId)}
								>
									Domain
								</MenuItem>
								<MenuItem value='sender_type'>Sender Type</MenuItem>
								<MenuItem value='specific_email'>Specific Email</MenuItem>
							</Select>
						</FormControl>

						<TextField
							label={getCriteriaLabel(criteriaType)}
							value={criteriaValue}
							onChange={(e) => setCriteriaValue(e.target.value)}
							sx={{ flexGrow: 1, mr: 1 }}
							disabled={isRunning || (inclusionEnabled && !tenantLevel)}
						/>

						<Button
							variant='contained'
							startIcon={<AddIcon />}
							onClick={handleAddEmailRule}
							disabled={isRunning || (!tenantLevel && inclusionEnabled)}
							sx={{ mt: 1 }}
						>
							Add
						</Button>
					</Box>
				</>
			)}

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<EmailRuleSection
					title='Domain Rules'
					type='domain'
					items={domainRules}
					handleDelete={handleDeleteEmailRule}
					disabled={
						isRunning || (inclusionEnabled && !tenantLevel) || hasConfig
					}
					tenantLevel={tenantLevel}
				/>

				<EmailRuleSection
					title='Sender Type Rules'
					type='sender_type'
					items={senderTypeRules}
					handleDelete={handleDeleteEmailRule}
					disabled={isRunning || (inclusionEnabled && !tenantLevel)}
					tenantLevel={tenantLevel}
				/>

				<EmailRuleSection
					title='Specific Email Rules'
					type='specific_email'
					items={specificEmailRules}
					handleDelete={handleDeleteEmailRule}
					disabled={isRunning || (inclusionEnabled && !tenantLevel)}
					tenantLevel={tenantLevel}
				/>
			</Box>
		</>
	);
};

EmailRules.propTypes = {
	tenantLevel: PropTypes.bool.isRequired,
	inclusionEnabled: PropTypes.bool.isRequired,
	handleRuleTypeChange: PropTypes.func.isRequired,
	isRunning: PropTypes.bool.isRequired,

	criteriaType: PropTypes.oneOf(['domain', 'sender_type', 'specific_email'])
		.isRequired,
	setCriteriaType: PropTypes.func.isRequired,

	criteriaValue: PropTypes.string.isRequired,
	setCriteriaValue: PropTypes.func.isRequired,

	handleAddEmailRule: PropTypes.func.isRequired,
	handleDeleteEmailRule: PropTypes.func.isRequired,

	domainRules: PropTypes.arrayOf(PropTypes.object).isRequired,
	senderTypeRules: PropTypes.arrayOf(PropTypes.object).isRequired,
	specificEmailRules: PropTypes.arrayOf(PropTypes.object).isRequired,

	hasConfig: PropTypes.bool.isRequired,
	reportId: PropTypes.string,
};

export default EmailRules;
